/** Assists in calculating the fractional indexing keys needed when reordering tasks within a list. */
// @ts-check
import { safeGenerateKeyBetween } from './listKeyHelpers';
import { computeTodayKeyboardReorderUpdates } from '@/features/today/utils/todayListReorderUtils';
import { computePlanRailKeyboardReorderUpdates } from '@/features/planning/utils/planRailReorderCompute';

/**
 * @param {Task[]} visibleSiblings
 * @param {Task[]} fullSiblings
 * @param {number} targetIndex
 * @param {boolean} isMovingDown
 */
const buildReorderKeys = (
  visibleSiblings,
  fullSiblings,
  targetIndex,
  isMovingDown,
) => {
  const swapTarget = isMovingDown
    ? visibleSiblings[targetIndex + 1]
    : visibleSiblings[targetIndex - 1];
  const fullIndex = fullSiblings.findIndex((t) => t.id === swapTarget?.id);

  if (isMovingDown) {
    return {
      previousKey: swapTarget?.orderKey ?? null,
      nextKey:
        fullIndex === -1 ? null : (fullSiblings[fullIndex + 1]?.orderKey ?? null),
    };
  }
  return {
    previousKey:
      fullIndex === -1 ? null : (fullSiblings[fullIndex - 1]?.orderKey ?? null),
    nextKey: swapTarget?.orderKey ?? null,
  };
};

const getParentPath = (path) => {
  if (!path) return '';
  const trimmed = path.endsWith('.') ? path.slice(0, -1) : path;
  const lastDot = trimmed.lastIndexOf('.');
  return lastDot === -1 ? '' : trimmed.slice(0, lastDot + 1);
};

const dotDepth = (path) => {
  if (!path || typeof path !== 'string') return 0;
  return (path.match(/\./g) || []).length;
};

const byOrderKey = (a, b) => {
  const keyA = a.orderKey || '';
  const keyB = b.orderKey || '';
  return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
};

const getFullSiblings = (taskToMove, allTasks) => {
  const path = taskToMove.materializedPath;
  const parentPath = getParentPath(path);
  const depth = dotDepth(path);
  return allTasks
    .filter(
      (t) =>
        t.materializedPath &&
        t.materializedPath.startsWith(parentPath) &&
        dotDepth(t.materializedPath) === depth,
    )
    .sort(byOrderKey);
};

const getReorderSiblings = (taskToMove, allTasks, filteredTasks) => {
  const path = taskToMove.materializedPath;
  const parentPath = getParentPath(path);
  const depth = dotDepth(path);
  const visibleIds = new Set(filteredTasks.map((t) => t.id));
  return allTasks
    .filter(
      (t) =>
        t.materializedPath &&
        t.materializedPath.startsWith(parentPath) &&
        dotDepth(t.materializedPath) === depth &&
        visibleIds.has(t.id),
    )
    .sort(byOrderKey);
};

const applyViewSpecificReorder = ({
  view,
  planMode,
  filteredTasks,
  taskToMove,
  isMovingDown,
  selectedTaskIdBeforeReorder,
  updateTask,
  userId,
}) => {
  const direction = isMovingDown ? 'down' : 'up';
  if (view === 'today') {
    const updates = computeTodayKeyboardReorderUpdates(
      filteredTasks,
      taskToMove.id,
      direction,
    );
    if (updates.length === 0) return true;
    selectedTaskIdBeforeReorder.current = taskToMove.id;
    updates.forEach(({ id, ...fields }) => updateTask(id, fields, userId));
    return true;
  }
  if (view === 'plan' && planMode) {
    const updates = computePlanRailKeyboardReorderUpdates(
      filteredTasks,
      taskToMove.id,
      direction,
    );
    if (updates.length === 0) return true;
    selectedTaskIdBeforeReorder.current = taskToMove.id;
    updates.forEach(({ id, ...fields }) => updateTask(id, fields, userId));
    return true;
  }
  return false;
};

const applySiblingOrderKeyReorder = ({
  taskToMove,
  allTasks,
  filteredTasks,
  isMovingDown,
  selectedTaskIdBeforeReorder,
  updateTask,
  userId,
}) => {
  const siblingTasks = getReorderSiblings(taskToMove, allTasks, filteredTasks);
  if (siblingTasks.length < 2) return;

  const actualIndex = siblingTasks.findIndex((t) => t.id === taskToMove.id);
  if (actualIndex === -1) return;
  if (isMovingDown && actualIndex >= siblingTasks.length - 1) return;
  if (!isMovingDown && actualIndex <= 0) return;

  const fullSiblingTasks = getFullSiblings(taskToMove, allTasks);
  const keys = buildReorderKeys(
    siblingTasks,
    fullSiblingTasks,
    actualIndex,
    isMovingDown,
  );
  const newOrderKey = safeGenerateKeyBetween(
    keys.previousKey ?? null,
    keys.nextKey ?? null,
  );
  selectedTaskIdBeforeReorder.current = taskToMove.id;
  updateTask(taskToMove.id, { orderKey: newOrderKey }, userId, {
    previousKey: keys.previousKey ?? null,
    nextKey: keys.nextKey ?? null,
  });
};

/**
 * @param {string} direction
 * @param {object} context
 */
export const handleReorder = (
  direction,
  {
    filteredTasks,
    selectedIndex,
    allTasks,
    updateTask,
    moveTask: _moveTask,
    userId,
    selectedTaskIdBeforeReorder,
    view,
    planMode,
  },
) => {
  const isMovingDown = direction === 'down';
  const taskToMove = filteredTasks[selectedIndex];
  if (!taskToMove) return;

  const handled = applyViewSpecificReorder({
    view,
    planMode,
    filteredTasks,
    taskToMove,
    isMovingDown,
    selectedTaskIdBeforeReorder,
    updateTask,
    userId,
  });
  if (handled) return;

  applySiblingOrderKeyReorder({
    taskToMove,
    allTasks,
    filteredTasks,
    isMovingDown,
    selectedTaskIdBeforeReorder,
    updateTask,
    userId,
  });
};
